import * as React from "react";
import { observer } from "mobx-react";

import { UitkLayoutConditionalGridColumnRow, UitkLayoutGrid, UitkLayoutGridItem } from "uitk-react-layout-grid";
import { UitkSpacing } from "uitk-react-spacing";

import { FlightTypeMenu } from "components/flexComponents/WizardFlightPWA/components/FlightTypeMenu";
import { FlightHiddenSearchDataHotwire } from "components/flexComponents/WizardFlightPWA/components/FlightHiddenSearchDataHotwire";
import { Dates } from "components/shared/Dates/Dates";
import { LobErrorSummary } from "components/shared/LobErrorSummary/LobErrorSummary";
import { Location } from "components/shared/Location/Location";
import { SubmitButton } from "components/shared/SubmitButton/SubmitButton";
import { Travelers } from "components/shared/Travelers/Travelers";
import { TravelersTriggerTypes } from "components/shared/TravelersField/typings";

import { FlightType } from "stores/wizard/state/typings";
import { useHotwireFlightOverrides } from "components/flexComponents/WizardFlightPWA/utils/hotwireFlightOverrides";
import { WizardFlightPWAViewProps, WizardFlightPWAFTNodeOptions } from "../typings";
import { normalizeOptionsData } from "src/stores/FlexTemplateStore";

export const WizardFlightPWAViewHotwire: React.FunctionComponent<WizardFlightPWAViewProps> = observer(
  ({
    onSelectFlightType,
    setInputsRefs,
    getLocError,
    flexModuleModelStore,
    flexTemplateNode,
    wizardState: { globalWizardState, flightWizardState },
  }) => {
    useHotwireFlightOverrides(flightWizardState);
    const {
      config: {
        form: { action },
      },
      subLOBState,
      errorInputRef,
      isRoundtripFormValid,
      isOnewayFormValid,
      numberOfErrors,
      updateInvalidFormsState,
      moreThanOneError,
    } = flightWizardState;

    const isRoundtrip = subLOBState === FlightType.ROUND_TRIP;
    const isOneway = subLOBState === FlightType.ONE_WAY;
    const model = flexModuleModelStore.getModel(flexTemplateNode.id);

    const options = normalizeOptionsData(flexTemplateNode, model) as WizardFlightPWAFTNodeOptions;

    const isFormValid = (isRoundtrip && isRoundtripFormValid) || (isOneway && isOnewayFormValid);

    if (isFormValid) {
      updateInvalidFormsState();
    }

    const mainGridColumns: UitkLayoutConditionalGridColumnRow = { small: 1, large: 2 };

    return (
      <form
        noValidate={true}
        className="WizardFlightPWAHotwire"
        action={action}
        onSubmit={flightWizardState.submit}
        id={flightWizardState.config.elementId}
        data-fm={options.fmId}
        data-fm-title-id={options.fmTitleId}
      >
        <FlightHiddenSearchDataHotwire lobState={flightWizardState} />
        <UitkSpacing padding={{ blockstart: "one", blockend: "one" }}>
          <div>
            <FlightTypeMenu
              onFlightTypeUpdate={onSelectFlightType}
              activeFlightType={subLOBState}
              showOneway={true}
              showMulticity={false}
            />
          </div>
        </UitkSpacing>
        <UitkLayoutGrid columns={{ small: 1, large: 3 }}>
          <UitkLayoutGridItem colSpan={2}>
            <UitkLayoutGrid columns={mainGridColumns} space="three">
              {!isFormValid && (
                <LobErrorSummary
                  locHeadingToken={getLocError(false)!}
                  locHeadingArgs={moreThanOneError() && [numberOfErrors]}
                  locLinkToken={getLocError(true)}
                  linkClickFocusId="flight-error"
                  inputErrorRef={errorInputRef}
                  colSpan={mainGridColumns}
                  setInputsRefs={setInputsRefs}
                />
              )}
              <Location
                setInputsRefs={setInputsRefs}
                originField={true}
                destinationField={true}
                lobState={flightWizardState}
                showSwapLocationsControl={false}
                colSpan={{ small: 1, large: 2 }}
                globalConfig={globalWizardState.config}
              />
              <Dates
                lobState={flightWizardState}
                colSpan={{ small: 1, large: 1 }}
                setInputsRefs={setInputsRefs}
                singleDate={isOneway}
                singleDateSelect={isRoundtrip}
              />
              <Travelers
                lobState={flightWizardState}
                config={flightWizardState.config.travelers}
                colSpan={{ small: 1 }}
                noRehydratationValues={flightWizardState.config.travelers.noRehydratationValues}
                triggerType={TravelersTriggerTypes.FAKE_INPUT}
              />
              <SubmitButton lobState={flightWizardState} colSpan={{ small: 1 }} />
            </UitkLayoutGrid>
          </UitkLayoutGridItem>
        </UitkLayoutGrid>
      </form>
    );
  }
);
